import { toast } from 'sonner';
import i18next from 'i18next';
import { useAssetGroupCriticalityEvaluationStore } from '@/store/asset-group-criticality-evaluation.store.js';
import { getIntegrationErrorMessage } from '@/lib/integration-error-message.js';

export class AssetGroupCriticalityEvaluationIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    _getErrorMessage(error, fallbackKey, fallbackDefault) {
        return getIntegrationErrorMessage(error);
    }

    _normalizeList(payload) {
        if (Array.isArray(payload)) return payload;
        if (Array.isArray(payload?.data)) return payload.data;
        return [];
    }

    async fetchAll(params = {}) {
        const store = useAssetGroupCriticalityEvaluationStore.getState();
        store.setLoading(true);
        store.clearError();

        try {
            const merged = { ...store.searchParams, ...params };
            const response = await this.service.getAll({
                page: merged.page,
                pageSize: merged.pageSize,
                search: merged.search || undefined,
                sortBy: merged.sortBy,
                sortOrder: merged.sortOrder,
            });

            const rows = this._normalizeList(response.data);
            store.setItems(rows);
            if (response.pagination) {
                store.setPaginationData({
                    total: response.pagination.total ?? 0,
                    totalPages: response.pagination.totalPages ?? 1,
                    page: response.pagination.page ?? merged.page,
                    pageSize: response.pagination.pageSize ?? merged.pageSize,
                });
            }
            if (response.uniqueValues) {
                store.setUniqueValues(response.uniqueValues);
            }

            return { success: true, data: rows, pagination: response.pagination };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesAssetGroupEvaluation:messages.fetchError',
                'Değerlendirme listesi yüklenemedi',
            );
            store.setError(message);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async assignExperts(assetGroupId, expertUserIds) {
        try {
            const response = await this.service.assignExperts(assetGroupId, { expertUserIds });
            toast.success(
                i18next.t('bigdesAssetGroupEvaluation:messages.assignExpertsSuccess', {
                    defaultValue: 'Subject matter experts assigned',
                }),
            );
            useAssetGroupCriticalityEvaluationStore.getState().updateItemInList(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesAssetGroupEvaluation:messages.assignExpertsError',
                'Uzman ataması yapılamadı',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async startSurvey(evaluationId, payload) {
        try {
            const response = await this.service.startSurvey(evaluationId, payload);
            toast.success(
                i18next.t('bigdesAssetGroupEvaluation:messages.startSurveySuccess', {
                    defaultValue: 'Survey started',
                }),
            );
            useAssetGroupCriticalityEvaluationStore.getState().updateItemInList(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesAssetGroupEvaluation:messages.startSurveyError',
                'Anket başlatılamadı',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async fetchMySurveys() {
        const store = useAssetGroupCriticalityEvaluationStore.getState();
        store.setMySurveysLoading(true);
        try {
            const response = await this.service.getMySurveys();
            const rows = this._normalizeList(response.data);
            store.setMySurveys(rows);
            return { success: true, data: rows };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesSurveyFilling:messages.fetchError',
                'Anket listesi yüklenemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setMySurveysLoading(false);
        }
    }

    async getSurveyDetail(evaluationId) {
        try {
            const response = await this.service.getSurveyDetail(evaluationId);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesSurveyFilling:messages.detailError',
                'Anket detayı alınamadı',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async submitSurvey(evaluationId, payload) {
        try {
            const response = await this.service.submitSurvey(evaluationId, payload);
            toast.success(
                i18next.t('bigdesSurveyFilling:messages.submitSuccess', {
                    defaultValue: 'Survey saved',
                }),
            );
            await this.fetchMySurveys();
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesSurveyFilling:messages.submitError',
                'Anket kaydedilemedi',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async getSummary(evaluationId) {
        try {
            const response = await this.service.getSummary(evaluationId);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesAssetGroupEvaluation:messages.summaryError',
                'Anket özeti alınamadı',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async getEvaluationPreview(evaluationId) {
        try {
            const response = await this.service.getEvaluationPreview(evaluationId);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesAssetGroupEvaluation:messages.previewError',
                'Değerlendirme verisi alınamadı',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async approve(evaluationId, payload = {}) {
        try {
            const response = await this.service.approve(evaluationId, payload);
            toast.success(
                i18next.t('bigdesAssetGroupEvaluation:messages.approveSuccess', {
                    defaultValue: 'Criticality level approved',
                }),
            );
            useAssetGroupCriticalityEvaluationStore.getState().updateItemInList(response.data);
            await this.fetchAll();
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesAssetGroupEvaluation:messages.approveError',
                'Onay işlemi başarısız',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async repeatSurvey(evaluationId) {
        try {
            const response = await this.service.repeatSurvey(evaluationId);
            toast.success(
                i18next.t('bigdesAssetGroupEvaluation:messages.repeatSurveySuccess', {
                    defaultValue: 'Survey restart started',
                }),
            );
            useAssetGroupCriticalityEvaluationStore.getState().updateItemInList(response.data);
            await this.fetchAll();
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesAssetGroupEvaluation:messages.repeatSurveyError',
                'Anket tekrarı başarısız',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }

    async restart(evaluationId, restartReason) {
        try {
            const response = await this.service.restart(evaluationId, { restartReason });
            toast.success(
                i18next.t('bigdesAssetGroupEvaluation:messages.restartSuccess', {
                    defaultValue: 'Evaluation restarted',
                }),
            );
            useAssetGroupCriticalityEvaluationStore.getState().updateItemInList(response.data);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(
                error,
                'bigdesAssetGroupEvaluation:messages.restartError',
                'Yeniden başlatma başarısız',
            );
            toast.error(message);
            return { success: false, error: message };
        }
    }
}
